import { makeStyles } from '@material-ui/styles';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Button,
	Checkbox,
	Drawer,
	FormControl, FormControlLabel,
	TextField,
	Typography
} from '@material-ui/core';
import { Close, Check, Remove, Add } from '@material-ui/icons';
import theme from '../../theme';
import Location from '../../../public/images/icons/location-thin.svg';
import { useTranslation } from 'tools/i18n';
import { useContext, useEffect, useState } from 'react';
import Link from 'next/link';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import FastDelivery from '../../../public/images/icons/fast-delivery-thin.svg';
import ExpandMore from '../../../public/images/icons/expand_more_light.svg';
import ProductFamily from '../../../public/images/icons/categories.svg';
import HandMade from '../../../public/images/icons/hand-made.svg';
import MeddelaMig from '../../../public/images/icons/meddela-mig.svg';
import { TrackingContext } from '../../providers/tracking';
import { getCategoryBreadcrumb, getProductFamily, getVariants } from '@functions/productOps';

import { ErrorContext } from "../../providers/error";
import parsePrices from "../../../functions/parseSWPrices";
import CampaignBlock from './blocks/campaignBlock';
import AddToWeddingList from '../../products/wishlist/AddToWeddingList';
import useSessionStore from '@services/stores/SessionStore';
import useCartStore from '@services/stores/CartStore';
import CallToActionButton from '@components/products/partials/CallToActionButton';
import LikeButton from '@components/products/wishlist/LikeButton';
import { useRouter } from 'next/router';
import StockIndicator from '@components/products/partials/StockIndicator';
import OutletParentProduct from '@components/page/product/partial/OutletParentProduct';

const useStyles = makeStyles(
	() => ({
		wrapper: {
			display: 'flex',
			marginTop: 15,
			marginBottom: 10,
			gap: 1,
			'&.soldOut': {
				flexDirection: 'column'
			},
			[theme.breakpoints.down('xs')]: {
				'&:not(.soldOut)': {
					display: 'none'
				}
			}
		},
		wrapperInner: {
			display: 'flex',
			gap: 1
		},
		checkmarks: {
			display: 'flex',
			width: '100%'
		},
		checkmark: {
			display: 'flex',
			alignItems: 'center',
			marginTop: 15,
			marginRight: 16,
			fontSize: 14,
			fontWeight: 300,
			color: theme.palette.primary.lowlight
		},
		checkIcon: {
			fontSize: 20,
			marginRight: 6,
			color: '#24bb81'
		},
		qtyWrapper: {
			display: 'flex',
			alignItems: 'center',
			backgroundColor: '#eaeaea'
		},
		qtyButton: {
			width: 42,
			height: 56,
			minWidth: 'auto',
			textAlign: 'center',
			appearance: 'none',
			cursor: 'pointer',
			[theme.breakpoints.down('sm')]: {
				width: 30,
			},
		},
		qtyIcon: {
			fontSize: 18,
			height: 25,
			weight: 25,
			fontWeight: 400
		},
		qtyInput: {
			height: 56,
			width: 42,
			textAlign: 'center',
			fontSize: 16,
			appearance: 'textfield',
			fontFamily: theme.typography.fontFamily,
			border: 'none',
			outline: 'none',
			backgroundColor: 'transparent',
			'&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
				appearance: 'none',
				margin: 0
			},
			[theme.breakpoints.down('sm')]: {
				width: 30,
			},
		},
		purchaseButton: {
			position: 'relative',
			...theme.button.purchase,
			width: '100%',
			padding: '16px 8px',
			fontSize: 12,
			color: theme.palette.primary.background,
			[theme.breakpoints.up('sm')]: {
				padding: '20px 8px',
				letterSpacing: 2
			},
			'&:before': {
				content: '""',
				height: 1,
				backgroundColor: theme.button.purchasePsuedo.background,
				display: 'block',
				position: 'absolute',
				top: 4,
				left: 0,
				right: 0,
				transition: 'all 0.25s ease'
			},
			'&:after': {
				content: '""',
				height: 1,
				backgroundColor: theme.button.purchasePsuedo.background,
				display: 'block',
				position: 'absolute',
				bottom: 4,
				left: 0,
				right: 0,
				transition: 'all 0.25s ease'
			},
			'&:hover': {
				backgroundColor: theme.button.purchaseHover.backgroundColor,
				'&:before, &:after': {
					left: 4,
					right: 4
				}
			},
		},
		purchaseButtonLabel: {
			flexDirection: 'column',
		},
		purchaseButtonDisabled: {
			color: `${theme.button.purchaseDisabled.color} !important`
		},
		soldOut: {
			width: '100%',
			backgroundColor: theme.button.purchaseDisabled.backgroundColor,
			'&:hover': {
				backgroundColor: theme.button.purchaseDisabled.backgroundColor
			},
		},
		nextShipmentText: {
			width: '100%',
			marginBottom: 10,
			padding: '8px 12px',
			fontSize: 12,
			letterSpacing: 0.5,
			textAlign: 'center',
			backgroundColor: '#f1f1f1',
			'& span': {
				fontWeight: 600
			},
			'&.outdoor': {
				color: "#009640"
			},
			'&.inline': {
				backgroundColor: 'transparent',
				marginBottom: 'auto',
				marginTop: '2px',
				padding: 'unset',
				fontSize: 'inherit',
				letterSpacing: 'inherit',
			}
		},
		subscribeWrapper: {
			marginTop: -10,
			padding: 20,
			backgroundColor: '#f1f1f1'
		},
		subscribeForm: {
			display: 'flex',
			flexDirection: 'row',
			color: theme.palette.primary.background,
			[theme.breakpoints.down('sm')]: {
				flexDirection: 'column'
			}
		},
		subscribeDescription: {
			marginTop: 0,
			marginBottom: 14
		},
		subscribeInputWrapper: {
			marginRight: 15,
			flexGrow: 1,
			[theme.breakpoints.down('sm')]: {
				marginRight: 0
			}
		},
		subscribeInput: {
			backgroundColor: theme.palette.primary.background
		},
		subscribeInputOutline: {
			border: 'none'
		},
		subscribeInputLabel: {
			transform: 'translate(14px, 4px) scale(0.75)'
		},
		subscribeButton: {
			padding: '10px 25px',
			fontSize: 12,
			fontWeight: 600,
			letterSpacing: 2,
			backgroundColor: theme.palette.primary.main,
			borderRadius: 0,
			color: theme.palette.primary.background,
			'&:hover': {
				backgroundColor: '#444444'
			},
			'.xmas &': {
				backgroundColor: theme.palette.primary.main
			},
			'&.success': {
				backgroundColor: '#0e6853',
			},
			[theme.breakpoints.down('sm')]: {
				padding: '14px 25px',
				marginTop: 10
			}
		},
		subscribeNote: {
			display: 'block',
			marginTop: 6,
			fontSize: 11,
			color: '#666',
			'& a': {
				color: 'inherit'
			}
		},
		stockStatus: {
			display: 'flex',
			alignItems: 'center',
			marginTop: 10,
			fontSize: 11,
			'&:before': {
				display: 'block',
				content: '""',
				width: 5,
				height: 5,
				marginRight: 4,
				backgroundColor: '#2ab382',
				borderRadius: '50%',
				[theme.breakpoints.up('md')]: {
					display: 'none'
				}
			},
			'&.almost-out-of-stock:before': {
				backgroundColor: '#e4a100'
			},
			'&.out-of-stock:before': {
				backgroundColor: '#d50025'
			},
			'& svg': {
				display: 'none',
				[theme.breakpoints.up('md')]: {
					display: 'block',
					width: 28,
					marginRight: 10
				}
			},
			[theme.breakpoints.up('md')]: {
				fontSize: 14
			}
		},

		fastDelivery: {
			display: 'flex',
			flexDirection: 'column',
			gridColumn: '1/3',
			margin: '10px 0 !important',
			fontSize: 14,
			color: "#009640",
			'& svg': {
				marginRight: 20,
				color: '#009640'
			}
		},
		fastDeliveryText: {
			marginTop: 5,
			marginLeft: 25,
			marginBottom: 0,
			fontSize: 12,
			color: '#009640',
			fontWeight: 300
		},
		freeShippingText: {
			position: 'relative',
			display: 'flex',
			alignItems: 'center',
			marginTop: 20,
			fontSize: 14,
			'& > svg': {
				marginRight: 20
			},
			'&.in-stock > svg': {
				color: 'green'
			},
			'&.order-to-stock > svg': {
				color: 'inherit',
			},
			'& a': {
				color: 'inherit'
			},
		},
		storeStockText: {
			display: 'flex',
			alignItems: 'center',
			marginTop: 20,
			fontSize: 14,
			cursor: 'pointer',
			textDecoration: 'underline',
			'& svg': {
				marginRight: 20
			}
		},
		singleStore: {
			display: 'flex',
			alignItems: 'flex-start',
			fontSize: 14,
			'& .store, & .stock': {
				fontSize: 'inherit'
			},
			'& .link': {
				marginTop: 4,
				fontSize: 'inherit',
				textDecoration: 'underline'
			}
		},
		usps: {
			display: 'flex',
			gridTemplateRows: 'auto auto',
			gridTemplateColumns: '1fr 1fr',
			flexDirection: 'column',
			width: '100%',
			gap: '15px 10px',
			marginTop: 10,
		},
		uspItem: {
			display: 'flex',
			alignItems: 'flex-start',
			alignSelf: 'flex-start',
			marginTop: 0,
			fontSize: 14,
			fontWeight: 300,
            color: 'rgba(0, 0, 0, 0.87)',
			'& svg': {
				flexShrink: 0,
				width: 15,
				marginRight: 10,
				strokeWidth: 0.5,
				'&.inStockMarker': {
					'& path': {
						stroke: 'green !important'
					}
				}
			},
			'&:first-of-type': {
				marginTop: '0px !important'
			},
		},
		likeLoader: {
			position: 'absolute',
			top: 0,
			left: 0,
		},
		stockPopup: {
			padding: '25px 15px',
			[theme.breakpoints.down('md')]: {
				maxHeight: '75vh',
				overflowY: 'auto'
			},
			[theme.breakpoints.up('md')]: {
				minWidth: 400
			}
		},
		stockPopupTitle: {
			display: 'flex',
			width: '100%',
			justifyContent: 'space-between',
			marginBottom: 25,
			paddingRight: 9,
			fontSize: 19,
			fontWeight: 600,
			fontFamily: '"Open Sans", sans-serif',
			textTransform: 'uppercase',
			'& svg': {
				width: 28,
				height: 28
			}
		},
		store: {
			fontSize: 11,
			fontWeight: 800,
			letterSpacing: 0.5,
			color: '#000000',
			[theme.breakpoints.up('md')]: {
				fontSize: 12
			}
		},
		stockIndicatorTweaker: {
			'&.indicator:before': {
				margin: '0 6px',
			}
		},
		stockIndicator: {
			display: 'flex',
			alignItems: 'center',
			marginTop: 2,
			fontSize: 11,
			fontWeight: 300,
			'&:before': {
				display: 'block',
				content: '""',
				width: 7,
				height: 7,
				marginRight: 6,
				backgroundColor: '#2ab382',
				borderRadius: '50%'
			},
			'&.almost-out-of-stock:before': {
				backgroundColor: '#ddd228'
			},
			'&.out-of-stock:before': {
				backgroundColor: '#d50025'
			},
			[theme.breakpoints.up('md')]: {
				fontSize: 13
			}
		},
		orderToStock: {
			color: '#ddd228'
		},
		accordion: {
			boxShadow: 'none',
			borderTop: '1px solid #7f7f7f',
			'&:before': {
				display: 'none'
			},
			'&:last-of-type': {
				borderBottom: '1px solid #7f7f7f'
			},
			[theme.breakpoints.down('sm')]: {
				backgroundColor: theme.palette.primary.background
			}
		},
		accordionExpanded: {
			margin: '0 0 25px 0 !important'
		},
		summary: {
			alignItems: 'flex-start',
			margin: '0 !important',
			padding: '14px 0 6px',
			[theme.breakpoints.up('sm')]: {
				minHeight: '0 !important',
				marginBottom: '0 !important'
			}
		},
		summaryIcon: {
			padding: 0,
			margin: '-12px 0 !important',
			color: '#000000',
			transform: 'scale(0.8)'
		},
		summaryContent: {
			display: 'flex',
			flexDirection: 'column',
			margin: 0,
			padding: 0
		},
		detailsRoot: {
			padding: 0
		},
		detailsText: {
			display: 'flex',
			gap: 20,
			width: '100%',
			fontSize: 11,
			fontWeight: 300,
			color: '#000000',
			lineHeight: 1.75,
			letterSpacing: 0.5,
			'& > *': {
				width: '50%'
			},
			'& a': {
				color: 'inherit'
			},
			[theme.breakpoints.up('md')]: {
				fontSize: 14
			}
		},
		disclaimer: {
			marginTop: 10,
			marginLeft: 'auto',
			textAlign: 'left',
			fontSize: 11,
			fontWeight: 300,
			fontStyle: 'italic'
		},
		klarnaWidget: {
			width: '100%',
			marginTop: 10,
			marginBottom: 10,
			gridColumn: '1/3',
			'& > div': {
				maxWidth: 'none !important'
			},
			'&::part(osm-container)': {
				maxWidth: 'none',
				minHeight: 0,
				width: '100%'
			},
			'&::part(osm-message),&::part(osm-cta)': {
  				fontSize: 14,
				[theme.breakpoints.down('sm')]: {
					  fontSize: 12
				}
			},
			[theme.breakpoints.down('sm')]: {
				marginTop: 0
			}
		},
		dotdWrapper: {
			position: 'relative',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			marginTop: 20,
			padding: 25,
			width: '100%',
			backgroundColor: '#000000',
			'&:before': {
				content: '""',
				position: 'absolute',
				top: 20,
				left: 20,
				right: 20,
				bottom: 20,
				zIndex: 1,
				pointerEvents: 'none',
				border: '1px solid transparent',
			},
			[theme.breakpoints.down('sm')]: {
				'&:before': {
					top: 15,
					left: 15,
					right: 15,
					bottom: 15,
				}
			}
		},
		dotdImage: {
			width: '100%',
			marginTop: 20,
			cursor: 'pointer',
			'& img': {
				display: 'block',
				maxWidth: '100%'
			}
		},
		dotdText: {
			marginTop: 5,
			marginBottom: 28,
			color: theme.palette.primary.background
		},
		dotdLink: {
			padding: '8px 20px',
			marginBottom: 20,
			borderRadius: 0,
			backgroundColor: '#f1f1f1',
			backgroundImage: 'linear-gradient(180deg, #f1f1f1, #a0a0a0)',
			color: '#000',
			textTransform: 'none',
		},
		consentSubscribeCheckbox: {
			fontSize: 14,
			marginTop: 10,
		},
	}),
	{ name: 'ProductBuy' }
);

export default function ProductBuy({ product, breadcrumbs, available, stock, productFamilyArticle }) {
	const classes = useStyles();
	const { t } = useTranslation(['general', 'popup', 'product-info']);
	const { config, paymentMethod, currentLanguage, salesChannel } = useSessionStore((state) => state.session);
	const cartActions = useCartStore((state) => state.methods);
	const { actions: trackingActions } = useContext(TrackingContext);
	const {actions: errorActions} = useContext(ErrorContext);
	const [quantity, setQuantity] = useState(1);
	const prices = parsePrices(product.calculatedPrices && product.calculatedPrices.length > 0 ? product.calculatedPrices[0] : product.calculatedPrice);
	const drawerBottom = useMediaQuery(theme.breakpoints.down('md'));
	const [disabled, setDisabled] = useState(false);
	const [stockPopup, setStockPopup] = useState(false);
	const [storesOpen, setStoresOpen] = useState({});
	const [subscribePopup, setSubscribePopup] = useState(true);
	const [subscribeEmail, setSubscribeEmail] = useState('');
	const [subscribeSuccess, setSubscribeSuccess] = useState(false);
	const [subscribeConsent, setSubscribeConsent] = useState(true);
	const [checkoutType, setCheckoutType] = useState(false);
	const [somethingInStock, setSomethingInStock] = useState(false);
	const customFields = product.translated.customFields;
	const router = useRouter();

	function changeQuantity(quantity) {
		setQuantity(parseInt(quantity, 10));
	}

	const stores = {
		NKPG: {
			name: 'Norrköping, Linden',
			address: 'Drottninggatan 41<br/>602 32 Norrköping',
			phone: '011 - 16 00 09',
			email: 'norrkoping@newport.se',
			openingHours: 'Mån - fre 10.00 - 19.00<br/>Lör 10.00 - 17.00<br/>Sön 12.00 - 16.00'
		},
		BRM: {
			name: 'Stockholm, Bromma Blocks',
			address: 'Ulvsundavägen 185–193<br/>168 67 Bromma<br/>',
			phone: '08 - 29 59 88',
			email: 'bromma@newport.se',
			openingHours: 'Mån - fre 10.00 - 20.00<br/>Lör 10.00 - 18.00<br/>Sön 10.00 - 18.00'
		},
		LKPG: {
			name: 'Linköping, Gränden',
			address: 'Nygatan 22<br/>582 19 Linköping<br/>',
			phone: '013 - 913 00 13',
			email: 'linkoping@newport.se',
			openingHours: 'Mån - fre 10.00 - 19.00<br/>Lör 10.00 - 17.00<br/>Sön 12.00 - 16.00'
		},
		NK: {
			name: 'Stockholm, NK',
			address: 'NK Hamngatan 18-20<br/>111 47 Stockholm<br/>',
			phone: '08 - 762 81 60',
			email: 'nk@newport.se',
			openingHours: 'Mån - fre 10.00 - 19.00<br/>Lör 10.00 - 18.00<br/>Sön 11.00 - 17.00'
		},
		VAS: {
			name: 'Västerås, Punkt Galleria',
			address: 'Stora Gatan 7-21<br/>722 12 Västerås<br/>',
			phone: '021 - 421 00 60',
			email: 'vasteras@newport.se',
			openingHours: 'Mån - fre 10.00 - 19.00<br/>Lör 10.00 - 17.00<br/>Sön 11.00 - 16.00'
		},
		ARE: {
			name: 'Åre, By',
			address: 'Årevägen 128<br/>837 52 Åre<br/>',
			phone: '0647 - 67 50 30',
			email: 'are@newport.se',
			openingHours: 'Mån - fre 11.00 - 17.00<br/>Lör 11.00 - 15.00<br/>Sön Stängt'
		},
		SSB: {
			name: 'Steninge slottsby',
			address: 'Steninge Slottsväg 141<br/>195 59 Märsta<br/>',
			phone: '08 - 502 573 90',
			email: 'steninge@newport.se',
			openingHours: 'Mån - fre 11.00 - 18.00<br/>Lör 10.00 - 17.00<br/>Sön 10.00 - 17.00'

		},
		// VASOUT: {
		// 	name: 'Västerås Outlet, Punkt Galleria',
		// 	address: 'Stora Gatan 7-21<br/>722 12 Västerås<br/>',
		// 	phone: '021 - 448 17 80',
		// 	email: 'outlet@newport.se',
		// 	openingHours: 'Mån - fre 10.00 - 19.00<br/>Lör 10.00 - 17.00<br/>Sön 11.00 - 16.00'
		// },
		NKPGOUT: {
			name: 'Norrköping Outlet, Ingelsta',
			address: 'Kromgatan 2<br/>602 23 Norrköping<br/>',
			phone: '011 - 443 39 90',
			email: 'outlet.norrkoping@newport.se',
			openingHours: 'Mån - fre 10.00 - 19.00<br/>Lör 10.00 - 17.00<br/>Sön 11.00 - 17.00'
		},
		NKGBG: {
			name: 'Göteborg, NK',
			address: 'Östra Hamngatan 42<br/>411 09 Göteborg<br/>',
			phone: '031 - 710 10 09',
			email: 'goteborg@newport.se',
			openingHours: 'Mån - fre 10.00 - 19.00<br/>Lör 10.00 - 18.00<br/>Sön 11.00 - 16.00'
		},
		NGA: {
			name: 'Stockholm, Newport Gallery',
			address: 'Regeringsgatan 48<br/>111 56 Stockholm<br/>',
			phone: '08 - 50 25 73 95',
			email: 'gallery@newport.se',
			openingHours: 'Mån - fre 10.00 - 19.00<br/>Lör 10.00 - 18.00<br/>Sön 11.00 - 17.00'
		}
	};

	useEffect(() => {
        let type = 'klarna';
        if (paymentMethod) {
            if (paymentMethod?.translated?.name !== 'Klarna Checkout') {
                type = 'adyen';
            }
        }

        setCheckoutType(type);
    }, [paymentMethod]);

	function increment() {
		changeQuantity(parseInt(quantity, 10) + 1);
	}

	function decrement() {
		if(quantity > 1) {
			changeQuantity(parseInt(quantity, 10) - 1);
		}
	}

	function onChange(value) {
		changeQuantity(value);
	}

	async function purchase(event) {
		await cartActions.addToCart({productId: product.id, quantity: quantity});
		purchaseTracking();
	}

	function purchaseTracking() {
		let price = product.calculatedPrices && product.calculatedPrices.length > 0 ? product.calculatedPrices[0] : product.calculatedPrice;

		trackingActions.addToCart({
            name: product.translated.name,
            id: product.productNumber,
            price: price.unitPrice,
            discount: Math.abs(prices?.discount?.amount),
            brand: product.manufacturer ? product.manufacturer.translated.name : '',
            category: getCategoryBreadcrumb(breadcrumbs),
            list: { id: '', name: 'add_to_cart_pdp', location: router.asPath},
            variant: getVariants(product),
            quantity: quantity,
            analytics: product?.extensions?.newport?.analytics,
        });
	}

	async function handlePurchase() {
		setDisabled(true);
		await purchase();
		setDisabled(false);
	}

	async function submitProductSubscribe() {
		const country = config?.language?.languageCode.split('-')[1];
		const storeId = salesChannel?.id;

		const subscribe = await fetch('/api/voyado/product/subscribe', {
			method: 'post',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({
				country,
				email: subscribeEmail,
				sku: product.productNumber,
				store: storeId,
				consent: subscribeConsent,
			})
		});

		const result = await subscribe.json();
		if (result.status === 'success') {
			//setSubscribePopup(false);
			setSubscribeSuccess(true);
		}
	}

	function isHandMade() {
		let handmadeArray = product?.properties?.filter((property) => {
			return property?.group?.translated?.customFields?.attributeCode === 'hand_made';
		});

		return handmadeArray?.length > 0;
	}

	return (
        <>
            {available ? (
                <>
                    <div className={classes.wrapper}>
                        <div className={classes.qtyWrapper}>
                            <Button onClick={decrement} className={[classes.qtyButton, 'decrement'].join(' ')}>
                                <Remove className={classes.qtyIcon} />
                            </Button>
                            <input
                                type="number"
                                value={quantity}
                                onChange={event => onChange(event.target.value)}
                                className={classes.qtyInput}
                            />
                            <Button onClick={increment} className={[classes.qtyButton, 'increment'].join(' ')}>
                                <Add className={classes.qtyIcon} />
                            </Button>
                        </div>

						<CallToActionButton
							addLines={true}
							onClick={handlePurchase}
							disabled={disabled}
							isLoading={disabled}
						>
							{t('buy')}
						</CallToActionButton>

						<LikeButton
							show={true}
							productId={product.id}
							hasLightButtons={false}
							size={'large'}
							analyticsName={product?.extensions?.newport?.analytics?.item_name}
						></LikeButton>
                    </div>
                </>
            ) : (
                <>
                    <div className={[classes.wrapper, 'soldOut'].join(' ')}>
						<div className={classes.wrapperInner}>
							<Button
								className={[
									classes.purchaseButton,
									classes.soldOut,
								].join(' ')}
								classes={{ disabled: classes.purchaseButton, label: classes.purchaseButtonLabel }}
								onClick={() => {}}
							>
								{product?.deliveryTime?.customFields?.delivery_time_texts_main_PDP || t('sold-out')}{' '}
							</Button>
							<LikeButton
								show={true}
								productId={product.id}
								hasLightButtons={false}
								size={'large'}
								analyticsName={product?.extensions?.newport?.analytics?.item_name}
							></LikeButton>
						</div>

                        {subscribePopup ? (
                            <div className={classes.subscribeWrapper}>
                                <p className={classes.subscribeDescription}>{t('sold-out-subscribe-description')}</p>
                                <form
                                    onSubmit={e => {
                                        e.preventDefault();
                                        submitProductSubscribe();
                                    }}
                                >
                                    <FormControl className={classes.subscribeForm}>
                                        <TextField
                                            type={'email'}
                                            name={'email'}
                                            variant={'filled'}
                                            className={classes.subscribeInputWrapper}
                                            label={t('popup:intro-email-placeholder')}
                                            value={subscribeEmail}
                                            onChange={event => {
                                                setSubscribeEmail(event.target.value);
                                            }}
                                            inputProps={{ className: classes.subscribeInput }}
                                            InputProps={{
                                                classes: { notchedOutline: classes.subscribeInputOutline },
                                                disableUnderline: true,
                                            }}
                                            required={true}
                                        />
                                        <Button type={'submit'} className={[classes.subscribeButton, subscribeSuccess ? 'success' : ''].join(' ')}>
											{subscribeSuccess ?
												<Check style={{ width: 20, marginLeft: 0 }} />
                                            :
												<>
													{t('sold-out-subscribe')}{' '}
													<MeddelaMig width={20} style={{ marginLeft: 10 }} />
												</>
											}
                                        </Button>
                                    </FormControl>
                                    <FormControlLabel
                                        className={classes.consentSubscribeCheckbox}
                                        control={
                                            <Checkbox
                                                defaultChecked={subscribeConsent}
                                                color="#000"
                                                size="small"
                                                onChange={event => {
                                                    setSubscribeConsent(event.target.checked);
                                                }}
                                            />
                                        }
                                        label={<small>{t('consent-additional-offers')}</small>}
                                    ></FormControlLabel>
                                    <span
                                        className={classes.subscribeNote}
                                        dangerouslySetInnerHTML={{ __html: t('sold-out-subscribe-note') }}
                                    ></span>
                                </form>
                            </div>
                        ) : (
                            ''
                        )}
                    </div>
                </>
            )}

			<AddToWeddingList productId={product.id} colorScheme={'bg-wedding-fresh'}></AddToWeddingList>

            {product.translated.customFields.fast_delivery_available && config.language.locale === 'sv' && stock > 0 ? (
                <div className={[classes.fastDelivery, classes.uspItem].join(' ')}>
                    <span>
                        <FastDelivery width={20} style={{ verticalAlign: 'text-top' }} />
                        Fast Delivery
                    </span>
                    <p className={classes.fastDeliveryText}>I lager och skickas inom 24h under helgfria vardagar</p>
                </div>
            ) : (
                ''
            )}

            <div className={classes.usps}>
				<StockIndicator
					className={classes.stockIndicatorTweaker}
					alternativeName={'pdp'}
					deliveryTime={product.deliveryTime}
				/>

                {config.language.locale === 'sv' && customFields.store_stock_levels ? (
                    <>
                        <span
                            className={[classes.storeStockText, classes.uspItem].join(' ')}
                            onClick={() => {
                                trackingActions.click({
                                    type: 'product_store_stock',
                                    text: 'open',
                                    identifier: product?.extensions?.newport?.analytics?.item_name,
                                });
                            }}
                        >
                            <div
                                onClick={() => {
                                    setStockPopup(true);
                                }}
                                style={{ display: 'flex', alignItems: 'center' }}
                            >
                                <Location width={20} className={somethingInStock ? 'inStockMarker' : ''}/>
                                {t('product-info:see-store-stock')}&nbsp;»
                            </div>
                        </span>

                        <Drawer
                            PaperProps={{ className: classes.stockPopup }}
                            anchor={drawerBottom ? 'bottom' : 'right'}
                            open={stockPopup}
                            onClose={() => {
                                setStockPopup(false);
                            }}
                        >
                            <label className={classes.stockPopupTitle}>
                                {t('attributes:store_stock')}{' '}
                                <Close
                                    fontSize="small"
                                    onClick={() => {
                                        setStockPopup(false);
                                    }}
                                />
                            </label>
                            <div className={classes.value}>
                                {Object.keys(stores)
                                    .sort((storeA, storeB) => {
                                        let stockLevelA = customFields['store_stock_levels'][storeA] || 0;
                                        let stockLevelB = customFields['store_stock_levels'][storeB] || 0;

                                        if (stockLevelB > stockLevelA) {
                                            return 1;
                                        } else if (stockLevelA === stockLevelB) {
                                            return 0;
                                        } else {
                                            return -1;
                                        }
                                    })
                                    .map(storeKey => {
                                        let stockLevel = customFields['store_stock_levels'][storeKey];
                                        let store = stores[storeKey];

										if (stockLevel > 2 && !somethingInStock) {
											setSomethingInStock(true)
										}

                                        return (
                                            <Accordion
                                                key={storeKey}
                                                classes={{
                                                    root: classes.accordion,
                                                    expanded: classes.accordionExpanded,
                                                }}
                                                square={true}
                                                expanded={!!storesOpen[storeKey]}
                                                onChange={() => {
                                                    let newStoresOpen = { ...storesOpen };

                                                    if (newStoresOpen[storeKey]) {
                                                        newStoresOpen[storeKey] = !newStoresOpen[storeKey];
                                                    } else {
                                                        newStoresOpen[storeKey] = true;
                                                    }

                                                    setStoresOpen({ ...newStoresOpen });
                                                }}
                                            >
                                                <AccordionSummary
                                                    classes={{
                                                        root: classes.summary,
                                                        content: classes.summaryContent,
                                                        expanded: classes.summary,
                                                        expandIcon: classes.summaryIcon,
                                                    }}
                                                    expandIcon={<ExpandMore width={48} />}
                                                >
                                                    <Typography className={classes.store} variant="body1">
                                                        {store.name}
                                                    </Typography>
                                                    {stockLevel > 10 ? (
                                                        <span
                                                            className={[classes.stockIndicator, 'in-stock'].join(' ')}
                                                        >
                                                            + 10 i lager
                                                        </span>
                                                    ) : (
                                                        ''
                                                    )}
                                                    {stockLevel <= 10 && stockLevel > 2 ? (
                                                        <span
                                                            className={[classes.stockIndicator, 'in-stock'].join(' ')}
                                                        >
                                                            {stockLevel} i lager
                                                        </span>
                                                    ) : (
                                                        ''
                                                    )}

                                                    {stockLevel <= 2 && stockLevel > 0 ? (
                                                        <span
                                                            className={[
                                                                classes.stockIndicator,
                                                                'almost-out-of-stock',
                                                            ].join(' ')}
                                                        >
                                                            Fåtal i lager, kontakta butiken för mer information
                                                        </span>
                                                    ) : (
                                                        ''
                                                    )}

                                                    {!stockLevel || stockLevel <= 0 ? (
                                                        <span
                                                            className={[classes.stockIndicator, 'out-of-stock'].join(
                                                                ' ',
                                                            )}
                                                        >
                                                            Ej i lager
                                                        </span>
                                                    ) : (
                                                        ''
                                                    )}
                                                </AccordionSummary>
                                                <AccordionDetails classes={{ root: classes.detailsRoot }}>
                                                    <div className={classes.detailsText}>
                                                        <div>
                                                            <p dangerouslySetInnerHTML={{ __html: store.address }} />
                                                            <div className={'phone'}>
                                                                <a
                                                                    href={
                                                                        'tel:' +
                                                                        store.phone.replace(/\s/g, '').replace('-', '')
                                                                    }
                                                                >
                                                                    {store.phone}
                                                                </a>
                                                            </div>
                                                            <div className={'email'}>
                                                                <a href={'mailto:' + store.email}>{store.email}</a>
                                                            </div>
                                                        </div>
                                                        <p dangerouslySetInnerHTML={{ __html: store.openingHours }} />
                                                    </div>
                                                </AccordionDetails>
                                            </Accordion>
                                        );
                                    })}

                                <Typography className={classes.disclaimer}>
                                    {t('attributes:store_stock_disclaimer')}
                                </Typography>
                            </div>
                        </Drawer>
                    </>
                ) : (
                    ''
                )}

                {productFamilyArticle ? (
                    <span className={[classes.freeShippingText, classes.uspItem].join(' ')}>
                        <ProductFamily width={20} />
                        <Link href={'/' + productFamilyArticle.seoUrls[0].seoPathInfo}>
                            <a>
                                {getProductFamily(product).indexOf('Collection') > 0
                                    ? t('general:product-family-collection', {
                                          productFamily: getProductFamily(product),
                                      })
                                    : t('general:product-family', { productFamily: getProductFamily(product) })}
                            </a>
                        </Link>
                    </span>
                ) : (
                    ''
                )}

                {isHandMade() ? (
                    <span className={[classes.freeShippingText, classes.uspItem].join(' ')}>
                        <HandMade width={20} />
                        {t('product-info:is-hand-made')}
                    </span>
                ) : (
                    ''
                )}
            </div>

            <CampaignBlock product={product} prices={prices} productFamily={productFamilyArticle}></CampaignBlock>
			<OutletParentProduct product={product}></OutletParentProduct>
        </>
    );
}
